import React, { useState, useEffect } from 'react';
import heroImage from '../assets/images/hero/villanyszerelo.jpg';
import logo from '../assets/images/logo.png';
import { FaBolt, FaSolarPanel, FaSnowflake, FaArrowRight, FaPhoneAlt } from 'react-icons/fa';
import '../App.css';

const Hero: React.FC = () => {
  const services = [
    {
      text: "Villanyszerelés",
      icon: <FaBolt className="inline-block mr-3 text-red-600" />
    },
    {
      text: "Napelem telepítés",
      icon: <FaSolarPanel className="inline-block mr-3 text-red-600" />
    },
    {
      text: "Klíma szerelés",
      icon: <FaSnowflake className="inline-block mr-3 text-red-600" />
    }
  ];

  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(false);
      setTimeout(() => {
        setCurrentServiceIndex((prev) => (prev + 1) % services.length);
        setIsAnimating(true);
      }, 100);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative h-[90vh] flex items-center bg-gradient-to-br from-gray-200 via-gray-300 to-gray-200">
      {/* Háttérkép villanyszerelővel */}
      <div className="absolute inset-0 z-0">
        <div className="relative h-full">
          <img 
            src={heroImage} 
            alt="Villanyszerelő" 
            className="w-full h-full object-cover object-[center_top] scale-125"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-transparent to-gray-200"></div>
        </div>
      </div>
      
      {/* Futurisztikus overlay elemek - csak nagyobb képernyőkön */}
      <div className="absolute inset-0 z-10 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-gray-200/70 to-transparent pulse-element"></div>
        <div className="absolute bottom-0 left-0 w-full h-20 bg-gradient-to-t from-gray-200/70 to-transparent pulse-element"></div>
        <div className="hidden lg:block absolute top-1/4 left-10 w-20 h-40 border-l-2 border-t-2 border-red-600/20 hero-border-animate"></div>
        <div className="hidden lg:block absolute bottom-1/4 right-10 w-20 h-40 border-r-2 border-b-2 border-red-600/20 hero-border-animate"></div>
        
        {/* Extra futurisztikus elemek - csak desktop */}
        <div className="hidden lg:block absolute top-10 right-1/4 w-4 h-4 bg-red-600/20 rounded-full pulse-element"></div>
        <div className="hidden lg:block absolute bottom-32 left-1/3 w-3 h-3 bg-red-600/20 rounded-full pulse-element"></div>
        <div className="hidden lg:block absolute top-1/3 right-32 w-32 h-1 bg-red-600/10 pulse-element"></div>
        <div className="hidden lg:block absolute bottom-1/3 left-32 w-32 h-1 bg-red-600/10 pulse-element"></div>
      </div>
      
      {/* Tartalom - mobil optimalizált */}
      <div className="container relative z-20 mx-auto px-4 py-12 md:py-16">
        <div className="max-w-3xl mx-auto md:mx-0">
          <div className="flex flex-col items-center md:items-start gap-12">
            <img 
              src={logo} 
              alt="P&K Electro Logo" 
              className="h-48 sm:h-56 md:h-72 lg:h-80 transition-transform duration-300 hover:scale-105 drop-shadow-xl hero-logo-animate"
            />
            <div className="flex flex-col items-center md:items-start gap-10 w-full">
              <div className="bg-black/80 backdrop-blur-sm rounded-xl p-8 w-full max-w-3xl hero-text-animate">
                <div className="relative h-[3rem] sm:h-[3.5rem] md:h-[4rem]">
                  {isAnimating && (
                    <p className="text-2xl sm:text-3xl md:text-4xl text-white font-medium text-center md:text-left gradient-text-animate text-change-animate flex items-center justify-center md:justify-start">
                      {services[currentServiceIndex].icon}
                      {services[currentServiceIndex].text}
                    </p>
                  )}
                </div>
                <div className="h-px bg-gradient-to-r from-red-600/20 via-red-600/40 to-red-600/20 my-4"></div>
                <p className="text-lg sm:text-xl text-gray-400 text-center md:text-left italic">
                  Több mint 5 év szakmai tapasztalat
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-6 justify-center md:justify-start w-full max-w-3xl hero-buttons-animate">
                <a 
                  href="#contact" 
                  className="group bg-gradient-to-r from-red-600 to-red-700 text-white font-bold py-5 px-8 text-xl rounded-xl 
                    transition-all duration-300 transform hover:scale-102 hover:shadow-[0_8px_30px_rgb(220,38,38,0.3)] 
                    flex items-center justify-center gap-3 relative overflow-hidden w-full sm:w-auto"
                >
                  <FaPhoneAlt className="text-xl transition-transform duration-300 group-hover:rotate-12" />
                  <span className="relative z-10">Ingyenes Felmérés</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-red-600 transform scale-x-0 transition-transform duration-300 origin-left group-hover:scale-x-100"></div>
                </a>
                <a 
                  href="#services" 
                  className="group bg-gradient-to-r from-gray-900 to-gray-800 text-white font-bold py-5 px-8 text-xl rounded-xl 
                    transition-all duration-300 hover:shadow-[0_8px_30px_rgb(0,0,0,0.2)] border border-gray-700
                    flex items-center justify-center gap-3 relative overflow-hidden w-full sm:w-auto"
                >
                  <span className="relative z-10">Szolgáltatásaink</span>
                  <FaArrowRight className="text-xl transition-transform duration-300 group-hover:translate-x-2" />
                  <div className="absolute inset-0 bg-gradient-to-r from-gray-800 to-gray-700 transform scale-x-0 transition-transform duration-300 origin-left group-hover:scale-x-100"></div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 